import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiType, FiEye, FiPause, FiKey, FiSun, FiVolume2, FiVolumeX, FiPlay, FiSquare } = FiIcons;

const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    textSize: 'medium', // small, medium, large
    highContrast: false,
    grayscale: false,
    readableFont: false,
    pauseAnimations: false,
    screenReader: false
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentText, setCurrentText] = useState('');
  const widgetRef = useRef(null);
  const buttonRef = useRef(null);
  const speechSynthesis = useRef(null);
  const currentUtterance = useRef(null);

  // Initialize speech synthesis
  useEffect(() => {
    if ('speechSynthesis' in window) {
      speechSynthesis.current = window.speechSynthesis;
    }
  }, []);

  // Apply accessibility settings to document
  useEffect(() => {
    const root = document.documentElement;

    // Text size
    root.classList.remove('text-small', 'text-medium', 'text-large');
    root.classList.add(`text-${settings.textSize}`);

    // High contrast
    if (settings.highContrast) {
      root.classList.add('high-contrast');
    } else {
      root.classList.remove('high-contrast');
    }

    // Grayscale
    if (settings.grayscale) {
      root.classList.add('grayscale-mode');
    } else {
      root.classList.remove('grayscale-mode');
    }

    // Readable font
    if (settings.readableFont) {
      root.classList.add('readable-font');
    } else {
      root.classList.remove('readable-font');
    }

    // Pause animations
    if (settings.pauseAnimations) {
      root.classList.add('pause-animations');
    } else {
      root.classList.remove('pause-animations');
    }
  }, [settings]);

  // Screen reader functionality
  useEffect(() => {
    if (settings.screenReader) {
      // Add click listeners to all text elements
      const handleElementClick = (e) => {
        const element = e.target;
        const textContent = getElementText(element);
        if (textContent) {
          speakText(textContent);
        }
      };

      // Add hover listeners for automatic reading
      const handleElementHover = (e) => {
        const element = e.target;
        if (element.tagName === 'BUTTON' || element.tagName === 'A' || element.tagName === 'INPUT') {
          const textContent = getElementText(element);
          if (textContent) {
            speakText(textContent);
          }
        }
      };

      document.addEventListener('click', handleElementClick);
      document.addEventListener('mouseenter', handleElementHover, true);

      return () => {
        document.removeEventListener('click', handleElementClick);
        document.removeEventListener('mouseenter', handleElementHover, true);
      };
    }
  }, [settings.screenReader]);

  // Handle outside click and ESC key
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (widgetRef.current && !widgetRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen]);

  const getElementText = (element) => {
    // Get meaningful text from element
    if (element.getAttribute('aria-label')) {
      return element.getAttribute('aria-label');
    }
    if (element.getAttribute('title')) {
      return element.getAttribute('title');
    }
    if (element.getAttribute('alt')) {
      return element.getAttribute('alt');
    }
    if (element.textContent && element.textContent.trim()) {
      return element.textContent.trim();
    }
    if (element.placeholder) {
      return `Input field: ${element.placeholder}`;
    }
    return '';
  };

  const speakText = (text) => {
    if (!speechSynthesis.current || !text) return;

    // Stop current speech
    speechSynthesis.current.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.8;
    utterance.pitch = 1;
    utterance.volume = 0.8;

    utterance.onstart = () => {
      setIsPlaying(true);
      setCurrentText(text);
    };

    utterance.onend = () => {
      setIsPlaying(false);
      setCurrentText('');
    };

    utterance.onerror = () => {
      setIsPlaying(false);
      setCurrentText('');
    };

    currentUtterance.current = utterance;
    speechSynthesis.current.speak(utterance);
  };

  const stopSpeaking = () => {
    if (speechSynthesis.current) {
      speechSynthesis.current.cancel();
      setIsPlaying(false);
      setCurrentText('');
    }
  };

  const toggleSetting = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const setTextSize = (size) => {
    setSettings(prev => ({
      ...prev,
      textSize: size
    }));
  };

  const showKeyboardHelp = () => {
    const helpText = `Keyboard Navigation Help:
• Tab: Navigate forward through interactive elements
• Shift + Tab: Navigate backward
• Enter/Space: Activate buttons and links
• Escape: Close dialogs and menus
• Arrow keys: Navigate within menus
• Home/End: Jump to beginning/end of content
• Page Up/Down: Scroll page content

This website is fully keyboard accessible. All interactive elements can be reached and activated using only the keyboard.`;

    if (settings.screenReader) {
      speakText(helpText);
    } else {
      alert(helpText);
    }
  };

  const readPageContent = () => {
    const mainContent = document.querySelector('main') || document.body;
    const headings = mainContent.querySelectorAll('h1, h2, h3, h4, h5, h6');
    let contentToRead = 'Page content: ';

    headings.forEach((heading, index) => {
      if (index < 5) { // Limit to first 5 headings
        contentToRead += `${heading.textContent.trim()}. `;
      }
    });

    speakText(contentToRead);
  };

  return (
    <div ref={widgetRef} className="accessibility-widget">
      {/* Toggle Button */}
      <motion.button
        ref={buttonRef}
        initial={{ x: -60 }}
        animate={{ x: isOpen ? 0 : -20 }}
        whileHover={{ x: 0 }}
        onClick={() => setIsOpen(!isOpen)}
        className="fixed left-0 top-1/2 transform -translate-y-1/2 z-50 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-r-full shadow-lg transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label={isOpen ? "Close accessibility options" : "Open accessibility options"}
        aria-expanded={isOpen}
        aria-controls="accessibility-panel"
      >
        <span className="text-xl" role="img" aria-label="Accessibility">
          🧑‍🦯
        </span>
      </motion.button>

      {/* Accessibility Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="accessibility-panel"
            initial={{ x: -300, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="fixed left-0 top-1/2 transform -translate-y-1/2 z-40 bg-white shadow-2xl rounded-r-lg border border-gray-200 p-3 sm:p-4 w-72 sm:w-80 max-w-[calc(100vw-20px)] max-h-[90vh] sm:max-h-96 overflow-y-auto"
            role="dialog"
            aria-labelledby="accessibility-title"
            aria-modal="true"
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 border-b border-gray-200">
              <h2 id="accessibility-title" className="text-base sm:text-lg font-semibold text-gray-900">
                Accessibility Options
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close accessibility options"
              >
                <SafeIcon icon={FiX} className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600" />
              </button>
            </div>

            {/* Screen Reader Section */}
            <div className="mb-3 sm:mb-4 p-2 sm:p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700">
                  <SafeIcon icon={settings.screenReader ? FiVolume2 : FiVolumeX} className="w-3 h-3 sm:w-4 sm:h-4" />
                  Screen Reader
                </label>
                <button
                  onClick={() => toggleSetting('screenReader')}
                  className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    settings.screenReader ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.screenReader}
                  aria-label="Toggle screen reader"
                >
                  <span
                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                      settings.screenReader ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {settings.screenReader && (
                <div className="space-y-2">
                  <div className="flex gap-1 sm:gap-2">
                    <button
                      onClick={readPageContent}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <SafeIcon icon={FiPlay} className="w-3 h-3 inline mr-1" />
                      Read Page
                    </button>
                    <button
                      onClick={stopSpeaking}
                      disabled={!isPlaying}
                      className="flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white px-2 sm:px-3 py-1.5 sm:py-2 rounded text-xs transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <SafeIcon icon={FiSquare} className="w-3 h-3 inline mr-1" />
                      Stop
                    </button>
                  </div>
                  {currentText && (
                    <div className="text-xs text-gray-600 p-2 bg-white rounded border">
                      <div className="font-medium mb-1">Now Reading:</div>
                      <div className="truncate">{currentText}</div>
                    </div>
                  )}
                  <div className="text-xs text-gray-600">
                    Click any text or hover over buttons to hear them read aloud.
                  </div>
                </div>
              )}
            </div>

            {/* Text Size */}
            <div className="mb-3 sm:mb-4">
              <h3 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <SafeIcon icon={FiType} className="w-3 h-3 sm:w-4 sm:h-4" />
                Text Size
              </h3>
              <div className="flex gap-1 sm:gap-2">
                {['small', 'medium', 'large'].map((size) => (
                  <button
                    key={size}
                    onClick={() => setTextSize(size)}
                    className={`px-2 sm:px-3 py-1 text-xs rounded-full border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      settings.textSize === size
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                    aria-pressed={settings.textSize === size}
                  >
                    {size.charAt(0).toUpperCase() + size.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Toggle Options */}
            <div className="space-y-2 sm:space-y-3">
              {/* High Contrast */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700">
                  <SafeIcon icon={FiSun} className="w-3 h-3 sm:w-4 sm:h-4" />
                  High Contrast
                </label>
                <button
                  onClick={() => toggleSetting('highContrast')}
                  className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    settings.highContrast ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.highContrast}
                  aria-label="Toggle high contrast mode"
                >
                  <span
                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                      settings.highContrast ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Grayscale */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700">
                  <SafeIcon icon={FiEye} className="w-3 h-3 sm:w-4 sm:h-4" />
                  Grayscale Mode
                </label>
                <button
                  onClick={() => toggleSetting('grayscale')}
                  className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    settings.grayscale ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.grayscale}
                  aria-label="Toggle grayscale mode"
                >
                  <span
                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                      settings.grayscale ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Readable Font */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700">
                  <SafeIcon icon={FiType} className="w-3 h-3 sm:w-4 sm:h-4" />
                  Readable Font
                </label>
                <button
                  onClick={() => toggleSetting('readableFont')}
                  className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    settings.readableFont ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.readableFont}
                  aria-label="Toggle readable font"
                >
                  <span
                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                      settings.readableFont ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              {/* Pause Animations */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-xs sm:text-sm font-medium text-gray-700">
                  <SafeIcon icon={FiPause} className="w-3 h-3 sm:w-4 sm:h-4" />
                  Pause Animations
                </label>
                <button
                  onClick={() => toggleSetting('pauseAnimations')}
                  className={`relative inline-flex h-5 w-9 sm:h-6 sm:w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    settings.pauseAnimations ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                  role="switch"
                  aria-checked={settings.pauseAnimations}
                  aria-label="Toggle pause animations"
                >
                  <span
                    className={`inline-block h-3 w-3 sm:h-4 sm:w-4 transform rounded-full bg-white transition-transform ${
                      settings.pauseAnimations ? 'translate-x-5 sm:translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* Keyboard Help */}
            <div className="mt-3 sm:mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={showKeyboardHelp}
                className="w-full flex items-center justify-center gap-2 px-3 sm:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <SafeIcon icon={FiKey} className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="text-xs sm:text-sm">Keyboard Help</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccessibilityWidget;